// src/components/dashboard/SettingsSection.tsx
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { apiService, PluginSettings, SavePluginSettingsRequest } from '../../services/api'
import { applyPrivacyMode } from '../../utils/format'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { 
  Save, 
  Loader2, 
  CheckCircle,
  AlertCircle,
  Plug,
  Lock
} from 'lucide-react'
import sellerrunningLogo from '../../assets/software-logos/sellerrunning.png'
import sellerflashLogo from '../../assets/software-logos/sellerflash.png'
import easycentralLogo from '../../assets/software-logos/easycentral.png'
import bringgoLogo from '../../assets/software-logos/bringgo.png'

export default function SettingsSection() {
  const { user } = useAuth()
  const [pluginSettings, setPluginSettings] = useState<PluginSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

    // Varsayılan yazılım değeri kullanıcıya göre belirlenir
  const getDefaultSoftware = () => {
    const email = localStorage.getItem('userEmail')?.toLowerCase()
    return email === 'bringgoship@gmail.com' ? 'BringGo Ship' : 'Sellerflash'
  }
  
  const [formData, setFormData] = useState({
    software: getDefaultSoftware(),
    cashbackPercentage: 0,
    usesEasyDepo: false,
    usesSellerRunningDepo: false,
    usesSellerFlashDepo: false
  })

  const [formErrors, setFormErrors] = useState({
    software: '',
    cashbackPercentage: ''

  })

  // ============================================================
  // GİZLİLİK MODU
  // Hesabı çalışan da kullanabildiği için kazanç tutarları maskelenir.
  // PIN olmadan açılamaz/kapatılamaz → çalışan maskeyi kaldıramaz.
  // ============================================================
  const [privacyEnabled, setPrivacyEnabled] = useState(false)
  const [privacyScope, setPrivacyScope] = useState<'extension' | 'panel' | 'both'>('both')
  const [hasPrivacyPin, setHasPrivacyPin] = useState(false)
  const [newPin, setNewPin] = useState('')
  const [newPin2, setNewPin2] = useState('')
  const [currentPin, setCurrentPin] = useState('')
  const [savingPrivacy, setSavingPrivacy] = useState(false)

  const privacyScopeOptions: { value: 'extension' | 'panel' | 'both'; label: string; desc: string }[] = [
    { value: 'extension', label: 'Eklenti', desc: 'Sadece Chrome eklentisindeki tutarlar gizlenir' },
    { value: 'panel', label: 'Panel', desc: 'Sadece bu paneldeki tutarlar gizlenir' },
    { value: 'both', label: 'Eklenti ve Panel', desc: 'Her ikisinde de tutarlar gizlenir' }
  ]

  const applyPrivacyFromSettings = (s: PluginSettings | null) => {
    const mode = (s?.privacyMode || 'none') as 'none' | 'extension' | 'panel' | 'both'
    setPrivacyEnabled(mode !== 'none')
    if (mode !== 'none') setPrivacyScope(mode)
    setHasPrivacyPin(!!s?.hasPrivacyPin)
    applyPrivacyMode(mode) // paneldeki maskeyi anında uygula
  }

  const handleSavePrivacy = async () => {
    const targetMode = privacyEnabled ? privacyScope : 'none'

    // Doğrulamalar
    if (hasPrivacyPin && !currentPin.trim()) {
      toast.error('Değişiklik için mevcut PIN gerekli')
      return
    }
    if (!hasPrivacyPin && privacyEnabled) {
      if (!/^\d{4,6}$/.test(newPin)) {
        toast.error('PIN 4-6 rakam olmalı')
        return
      }
      if (newPin !== newPin2) {
        toast.error('PIN tekrarı eşleşmiyor')
        return
      }
    }
    if (newPin && !/^\d{4,6}$/.test(newPin)) {
      toast.error('PIN 4-6 rakam olmalı')
      return
    }
    if (newPin && newPin !== newPin2) {
      toast.error('PIN tekrarı eşleşmiyor')
      return
    }

    try {
      setSavingPrivacy(true)
      const updated = await apiService.savePrivacySettings({
        privacyMode: targetMode,
        newPin: newPin || undefined,
        currentPin: currentPin || undefined
      })
      setPluginSettings(updated)
      applyPrivacyFromSettings(updated)
      setNewPin(''); setNewPin2(''); setCurrentPin('')

      // Eklentiye anında senkron sinyali (mevcut mekanizma)
      window.postMessage({ type: 'EBH_REFRESH_PLUGIN_SETTINGS' }, window.location.origin)

      toast.success(targetMode === 'none' ? 'Gizlilik modu kapatıldı' : 'Gizlilik modu kaydedildi')
    } catch (error: any) {
      toast.error(error?.message?.includes('PIN') ? error.message : (error?.message || 'Gizlilik ayarı kaydedilemedi'))
    } finally {
      setSavingPrivacy(false)
    }
  }

  // bringgoship@gmail.com kullanıcısı için özel yazılım seçeneği
  const isBringGoUser = user?.email?.toLowerCase() === 'bringgoship@gmail.com'

  const softwareOptions = isBringGoUser
    ? [{ value: 'BringGo Ship', label: 'BringGo Ship', disabled: false, logo: bringgoLogo }]
    : [
        { value: 'Sellerrunning', label: 'SellerRunning', disabled: false, logo: sellerrunningLogo },
        { value: 'Sellerflash', label: 'SellerFlash', disabled: false, logo: sellerflashLogo },
        { value: 'Easycentral', label: 'EasyCentral', disabled: false, logo: easycentralLogo }
      ]

  useEffect(() => {
    fetchPluginSettings()
  }, [])

  const fetchPluginSettings = async () => {
    try {
      setLoading(true)
      const settings = await apiService.getPluginSettings()
      setPluginSettings(settings)
      applyPrivacyFromSettings(settings) // gizlilik modunu forma + maskeye uygula

      // Form data'yı doldur
      setFormData({
        software: settings.software || getDefaultSoftware(),
        cashbackPercentage: settings.cashbackPercentage || 0,
        usesEasyDepo: settings.usesEasyDepo ?? false,
        usesSellerRunningDepo: settings.usesSellerRunningDepo ?? false,
        usesSellerFlashDepo: settings.usesSellerFlashDepo ?? false
      })
    } catch (error: any) {
      console.error('Plugin settings fetch error:', error)
      
      // Eğer henüz ayar yoksa (404), varsayılan değerlerle devam et
      if (error.message?.includes('404')) {
        const defaultSoftware = getDefaultSoftware()
        setPluginSettings({
          id: '',
          software: defaultSoftware,
          cashbackPercentage: 0,
          usesEasyDepo: false,
          usesSellerRunningDepo: false,
          usesSellerFlashDepo: false,
          buyerEmail: null,
          privacyMode: 'none',
          hasPrivacyPin: false,
          updatedAtUtc: new Date().toISOString()
        })
      } else {
        toast.error('Ayarlar yüklenemedi')
      }
    } finally {
      setLoading(false)
    }
  }

  const validateForm = (): boolean => {
    const errors = {
      software: '',
      cashbackPercentage: '',
      buyerEmail: ''
    }

    let isValid = true

    if (!formData.software.trim()) {
      errors.software = 'Yazılım türü gerekli'
      isValid = false
    }

    if (formData.cashbackPercentage < 0 || formData.cashbackPercentage > 100) {
      errors.cashbackPercentage = 'Cashback yüzdesi 0-100 arasında olmalı'
      isValid = false
    }


    setFormErrors(errors)
    return isValid
  }

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Lütfen form hatalarını düzeltin')
      return
    }

    try {
      setSaving(true)

      const payload: SavePluginSettingsRequest = {
        software: formData.software.trim(),
        cashbackPercentage: formData.cashbackPercentage,
        usesEasyDepo: formData.software.trim().toLowerCase() === 'easycentral' ? formData.usesEasyDepo : false,
        usesSellerRunningDepo: formData.software.trim().toLowerCase() === 'sellerrunning' ? formData.usesSellerRunningDepo : false,
        usesSellerFlashDepo: formData.software.trim().toLowerCase() === 'sellerflash' ? formData.usesSellerFlashDepo : false
      }

      const updated = await apiService.savePluginSettings(payload)
      setPluginSettings(updated)

      // Chrome eklentisine anında senkronizasyon sinyali
      window.postMessage({ type: 'EBH_REFRESH_PLUGIN_SETTINGS' }, window.location.origin)
      
      toast.success('Eklenti ayarları başarıyla kaydedildi!')
    } catch (error: any) {
      console.error('Plugin settings save error:', error)
      const errorMessage = error.message || 'Ayarlar kaydedilemedi'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleInputChange = (field: keyof typeof formData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user types
    if (field in formErrors) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center py-12"
      >
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </motion.div>
    )
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="glass border-orange-200 shadow-lg max-w-3xl mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Plug className="w-5 h-5 text-orange-600" />
            <CardTitle className="text-lg font-semibold text-gray-800">
              Eklenti Ayarları
            </CardTitle>
          </div>
          <CardDescription className="text-gray-600">
            Chrome eklentisi ve cashback ayarlarınızı yapılandırın
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Software Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Yazılım Türü
            </label>
            <div className="space-y-2">
              {softwareOptions.map((option) => {
                const isSelected = formData.software === option.value && !option.disabled
                const isEasycentral = option.value === 'Easycentral'
                const isSellerrunning = option.value === 'Sellerrunning'
                const isSellerflash = option.value === 'Sellerflash'
                return (
                  <div
                    key={option.value}
                    className={`border rounded-lg transition-all overflow-hidden ${
                      option.disabled
                        ? 'border-gray-200 bg-gray-50'
                        : isSelected
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-orange-200 hover:border-orange-300'
                    }`}
                  >
                    <label className={`flex items-center justify-between p-3 ${option.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="software"
                          value={option.value}
                          checked={formData.software === option.value}
                          onChange={(e) => handleInputChange('software', e.target.value)}
                          disabled={option.disabled}
                          className="text-orange-600 focus:ring-orange-500"
                        />
                        {option.logo && (
                          <img
                            src={option.logo}
                            alt=""
                            className={`w-6 h-6 object-contain rounded-sm flex-shrink-0 ${option.disabled ? 'opacity-40 grayscale' : ''}`}
                          />
                        )}
                        <span className={`font-medium ${option.disabled ? 'text-gray-400' : 'text-gray-800'}`}>
                          {option.label}
                        </span>
                      </div>
                      {option.disabled && (
                        <div className="flex items-center gap-2 text-xs">
                          <Lock className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-gray-500 font-medium">Work in Progress</span>
                        </div>
                      )}
                    </label>

                    {isEasycentral && isSelected && (
                      <div className="border-t border-orange-200 bg-white/60 px-4 py-3">
                        <p className="text-xs font-medium text-gray-600 mb-2">
                          EasyDepo kullanıyor musunuz?
                        </p>
                        <div className="flex gap-2">
                          <label className={`flex-1 flex items-center justify-center gap-2 py-1.5 px-3 border rounded-md cursor-pointer text-sm transition-all ${
                            formData.usesEasyDepo
                              ? 'border-orange-500 bg-orange-100 text-orange-800 font-medium'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300'
                          }`}>
                            <input
                              type="radio"
                              name="usesEasyDepo"
                              checked={formData.usesEasyDepo === true}
                              onChange={() => handleInputChange('usesEasyDepo', true)}
                              className="text-orange-600 focus:ring-orange-500 w-3.5 h-3.5"
                            />
                            Evet
                          </label>
                          <label className={`flex-1 flex items-center justify-center gap-2 py-1.5 px-3 border rounded-md cursor-pointer text-sm transition-all ${
                            !formData.usesEasyDepo
                              ? 'border-orange-500 bg-orange-100 text-orange-800 font-medium'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300'
                          }`}>
                            <input
                              type="radio"
                              name="usesEasyDepo"
                              checked={formData.usesEasyDepo === false}
                              onChange={() => handleInputChange('usesEasyDepo', false)}
                              className="text-orange-600 focus:ring-orange-500 w-3.5 h-3.5"
                            />
                            Hayır
                          </label>
                        </div>
                        <p className="mt-2 text-[11px] text-gray-500 leading-relaxed">
                          {formData.usesEasyDepo
                            ? 'Depo adresi checkout\'ta otomatik doldurulup onaylanır.'
                            : 'Checkout\'ta adres düzenleme ekranı açılır, adresi manuel girersiniz.'}
                        </p>
                      </div>
                    )}

                    {isSellerrunning && isSelected && (
                      <div className="border-t border-orange-200 bg-white/60 px-4 py-3">
                        <p className="text-xs font-medium text-gray-600 mb-2">
                          Ara depo kullanıyor musunuz?
                        </p>
                        <div className="flex gap-2">
                          <label className={`flex-1 flex items-center justify-center gap-2 py-1.5 px-3 border rounded-md cursor-pointer text-sm transition-all ${
                            formData.usesSellerRunningDepo
                              ? 'border-orange-500 bg-orange-100 text-orange-800 font-medium'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300'
                          }`}>
                            <input
                              type="radio"
                              name="usesSellerRunningDepo"
                              checked={formData.usesSellerRunningDepo === true}
                              onChange={() => handleInputChange('usesSellerRunningDepo', true)}
                              className="text-orange-600 focus:ring-orange-500 w-3.5 h-3.5"
                            />
                            Evet
                          </label>
                          <label className={`flex-1 flex items-center justify-center gap-2 py-1.5 px-3 border rounded-md cursor-pointer text-sm transition-all ${
                            !formData.usesSellerRunningDepo
                              ? 'border-orange-500 bg-orange-100 text-orange-800 font-medium'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300'
                          }`}>
                            <input
                              type="radio"
                              name="usesSellerRunningDepo"
                              checked={formData.usesSellerRunningDepo === false}
                              onChange={() => handleInputChange('usesSellerRunningDepo', false)}
                              className="text-orange-600 focus:ring-orange-500 w-3.5 h-3.5"
                            />
                            Hayır
                          </label>
                        </div>
                        <p className="mt-2 text-[11px] text-gray-500 leading-relaxed">
                          {formData.usesSellerRunningDepo
                            ? 'Depo adresi checkout\'ta otomatik doldurulup sipariş akışı otomatik ilerler.'
                            : 'Otomatik akış kapalı; siparişi SellerRunning\'in kendi akışıyla verirsiniz.'}
                        </p>
                      </div>
                    )}

                    {isSellerflash && isSelected && (
                      <div className="border-t border-orange-200 bg-white/60 px-4 py-3">
                        <p className="text-xs font-medium text-gray-600 mb-2">
                          Depo adresi otomatik doldurulsun mu?
                        </p>
                        <div className="flex gap-2">
                          <label className={`flex-1 flex items-center justify-center gap-2 py-1.5 px-3 border rounded-md cursor-pointer text-sm transition-all ${
                            formData.usesSellerFlashDepo
                              ? 'border-orange-500 bg-orange-100 text-orange-800 font-medium'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300'
                          }`}>
                            <input
                              type="radio"
                              name="usesSellerFlashDepo"
                              checked={formData.usesSellerFlashDepo === true}
                              onChange={() => handleInputChange('usesSellerFlashDepo', true)}
                              className="text-orange-600 focus:ring-orange-500 w-3.5 h-3.5"
                            />
                            Evet
                          </label>
                          <label className={`flex-1 flex items-center justify-center gap-2 py-1.5 px-3 border rounded-md cursor-pointer text-sm transition-all ${
                            !formData.usesSellerFlashDepo
                              ? 'border-orange-500 bg-orange-100 text-orange-800 font-medium'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300'
                          }`}>
                            <input
                              type="radio"
                              name="usesSellerFlashDepo"
                              checked={formData.usesSellerFlashDepo === false}
                              onChange={() => handleInputChange('usesSellerFlashDepo', false)}
                              className="text-orange-600 focus:ring-orange-500 w-3.5 h-3.5"
                            />
                            Hayır
                          </label>
                        </div>
                        <p className="mt-2 text-[11px] text-gray-500 leading-relaxed">
                          {formData.usesSellerFlashDepo
                            ? 'Depo adresi checkout\'ta otomatik doldurulup sipariş akışı otomatik ilerler.'
                            : 'Otomatik akış kapalı; siparişi SellerFlash\'in kendi akışıyla verirsiniz.'}
                        </p>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

              {!isBringGoUser && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">⚠️ Önemli Bilgilendirme</p>
                      <p>
                        Farklı yazılım, aradepo veya altyapı kullanıyorsanız lütfen{' '}
                        <span className="font-semibold">Manuel Sipariş</span> butonundan sipariş verin ya da
                        bizimle iletişime geçerek size <span className="font-semibold">özel kurulum</span> talep edebilirsiniz.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            {formErrors.software && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {formErrors.software}
              </p>
            )}
          </div>

          {/* Cashback Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cashback Yüzdesi (%)
            </label>
            {/* Bilgilendirme Kutusu */}
  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
    <div className="flex gap-2">
      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-blue-800">
        <p className="font-medium mb-1">ℹ️ Cashback Oranı Nedir?</p>
        <p>
          Bu alan, alıcı hesabında ürün alımı yapılmak için kullanılan kartın cashback oranıdır. 
          Eğer böyle bir cashback kartınız yoksa bu alanı <span className="font-semibold">0</span> olarak bırakmalısınız.
        </p>
      </div>
    </div>
  </div>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.cashbackPercentage}
              onChange={(e) => handleInputChange('cashbackPercentage', parseFloat(e.target.value) || 0)}
              className={`${formErrors.cashbackPercentage ? 'border-red-500' : 'border-orange-200'}`}
            />
            {formErrors.cashbackPercentage && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {formErrors.cashbackPercentage}
              </p>
            )}
          </div>


          {/* Last Updated */}
          {pluginSettings?.id && (
            <div className="pt-4 border-t border-orange-100">
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5" />
                Son Güncelleme: {new Date(pluginSettings.updatedAtUtc).toLocaleString('tr-TR')}
              </p>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Ayarları Kaydet
              </>
            )}
          </Button>
          {/* 🧩 Eklenti İndirme Bölümü */}
<div className="pt-8 border-t border-orange-100">
  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-2">
    <Plug className="w-5 h-5 text-orange-600" />
    EarnBackHub Chrome Eklentisi
  </h3>
  <p className="text-gray-600 text-sm mb-4">
    EarnBackHub eklentisini indirip Chrome’a yükleyerek sipariş ve cashback verilerini otomatik olarak senkronize edebilirsiniz.
  </p>

  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-sm text-gray-700 mb-1"><span className="font-medium">Sürüm:</span> v5.8.0</p>
        <p className="text-sm text-gray-700 mb-1"><span className="font-medium">Son Güncelleme:</span> 17 Ekim 2026</p>
        <p className="text-sm text-gray-700"><span className="font-medium">Uyumlu Tarayıcılar:</span> Chrome, Edge, Brave, Opera</p>
      </div>
      <div className="mt-4 sm:mt-0 flex gap-2">
        <a
          href="https://drive.google.com/uc?export=download&id=1cwDrwgf8h0aS86TQKrf3hqzsHHwNGE7Z"
          download
          className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-sm px-4 py-2 rounded-lg shadow transition-transform transform hover:scale-105"
        >
          <Save className="w-4 h-4" />
          Eklentiyi İndir
        </a>
        <a
          href=""
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-orange-300 text-orange-700 hover:bg-orange-50 text-sm px-4 py-2 rounded-lg transition"
        >
          <AlertCircle className="w-4 h-4" />
          Kurulum Rehberi
        </a>
      </div>
    </div>
  </div>

  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 flex items-start gap-2">
    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
    <p>
      <span className="font-semibold">Kurulum:</span> Eklentiyi indirdikten sonra 
      <span className="font-semibold"> chrome://extensions </span> sayfasına gidin → “Geliştirici modu”nu aktif edin → 
      “Paketlenmemiş öğe yükle” butonuyla klasörü seçin.
    </p>
  </div>
</div>
        </CardContent>
      </Card>

      {/* ============================================================
          GİZLİLİK MODU
          Hesabı çalışan da kullanabildiği için kazanç tutarları maskelenir.
          PIN olmadan açılamaz/kapatılamaz → çalışan maskeyi kaldıramaz.
         ============================================================ */}
      <Card className="glass border-slate-200 shadow-lg max-w-3xl mx-auto mt-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-slate-700" />
            <CardTitle className="text-lg font-semibold text-gray-800">Gizlilik Modu</CardTitle>
          </div>
          <CardDescription className="text-gray-600">
            Kazanç tutarlarını gizler. Hesabı başkası (ör. çalışanınız) kullanıyorsa
            tutarları göremez; açmak ve kapatmak PIN gerektirir.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          {/* Ana toggle */}
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
            <div>
              <p className="font-medium text-gray-800">Tutarları gizle</p>
              <p className="text-sm text-gray-500">
                {privacyEnabled ? 'Aktif — seçilen yerlerde tutarlar ●●●● görünür' : 'Kapalı — tutarlar normal görünür'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPrivacyEnabled(v => !v)}
              className={`relative h-7 w-12 flex-none rounded-full transition-colors ${privacyEnabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
              aria-pressed={privacyEnabled}
            >
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${privacyEnabled ? 'left-6' : 'left-1'}`} />
            </button>
          </div>

          {/* Kapsam seçimi */}
          {privacyEnabled && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Nerede gizlensin?</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {privacyScopeOptions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPrivacyScope(opt.value)}
                    className={`rounded-lg border p-3 text-left transition ${
                      privacyScope === opt.value
                        ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-300'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-sm font-semibold text-gray-800">{opt.label}</div>
                    <div className="mt-0.5 text-xs text-gray-500">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* PIN alanları */}
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
            {hasPrivacyPin ? (
              <>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">PIN tanımlı.</span> Herhangi bir değişiklik için mevcut PIN gerekir.
                </p>
                <Input
                  type="password" inputMode="numeric" maxLength={6}
                  placeholder="Mevcut PIN"
                  value={currentPin}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentPin(e.target.value.replace(/\D/g, ''))}
                  className="max-w-[200px]"
                />
                <details className="text-sm text-gray-600">
                  <summary className="cursor-pointer select-none">PIN'i değiştir</summary>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Input type="password" inputMode="numeric" maxLength={6} placeholder="Yeni PIN"
                      value={newPin} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPin(e.target.value.replace(/\D/g, ''))} className="max-w-[160px]" />
                    <Input type="password" inputMode="numeric" maxLength={6} placeholder="Yeni PIN (tekrar)"
                      value={newPin2} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPin2(e.target.value.replace(/\D/g, ''))} className="max-w-[160px]" />
                  </div>
                </details>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-700">
                  Gizlilik modunu açmak için bir <span className="font-medium">PIN belirleyin</span> (4-6 rakam).
                  Bu PIN olmadan mod kapatılamaz.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Input type="password" inputMode="numeric" maxLength={6} placeholder="PIN"
                    value={newPin} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPin(e.target.value.replace(/\D/g, ''))} className="max-w-[160px]" />
                  <Input type="password" inputMode="numeric" maxLength={6} placeholder="PIN (tekrar)"
                    value={newPin2} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPin2(e.target.value.replace(/\D/g, ''))} className="max-w-[160px]" />
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={handleSavePrivacy} disabled={savingPrivacy}
              className="bg-slate-800 hover:bg-slate-900 text-white">
              {savingPrivacy ? 'Kaydediliyor…' : 'Gizlilik Ayarını Kaydet'}
            </Button>
            <p className="text-xs text-gray-500">Sipariş sayıları ve tarihler görünür kalır.</p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}
